/**
 * GlobalNav - Shared navigation bar displayed on all pages
 *
 * Desktop: horizontal links (Home, 8 parts, Compatibility)
 * Mobile: hamburger menu with dropdown
 */

import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Cpu, Gpu, MemoryStick, HardDrive, Zap, PcCase, Fan, CircuitBoard } from 'lucide-react'
import { ComponentType } from '../models/pc-build-model'
import { UNIFIED_COMPONENT_CONFIG } from '../config/componentConfig'
import { TOOL_CONFIG } from '../config/toolConfig'
import { useScrollDirection } from '../hooks/useScrollDirection'
import { useAuth } from '../context/AuthContext'
import type { ThemeColor } from '../types/theme'

interface NavItem {
  label: string
  path: string
  themeColor: ThemeColor
  icon: React.ReactNode
}

// Matches TOP page order: CPU → MB → GPU → Memory → Storage → PSU → Case → Cooler
const COMPONENT_ORDER: ComponentType[] = [
  ComponentType.CPU,
  ComponentType.MOTHERBOARD,
  ComponentType.GPU,
  ComponentType.MEMORY,
  ComponentType.STORAGE,
  ComponentType.PSU,
  ComponentType.CASE,
  ComponentType.CPU_COOLER,
]

const componentIconMap: Partial<Record<string, React.ReactNode>> = {
  cpu:          <Cpu className="w-4 h-4" />,
  motherboard:  <CircuitBoard className="w-4 h-4" />,
  gpu:          <Gpu className="w-4 h-4" />,
  memory:       <MemoryStick className="w-4 h-4" />,
  storage:      <HardDrive className="w-4 h-4" />,
  psu:          <Zap className="w-4 h-4" />,
  case:         <PcCase className="w-4 h-4" />,
  cooler:       <Fan className="w-4 h-4" />,
}

const NAV_ITEMS: NavItem[] = [
  // Tools (shown first, matching TOP page hero section) — sourced from TOOL_CONFIG
  { label: TOOL_CONFIG.COMPATIBILITY.label, path: TOOL_CONFIG.COMPATIBILITY.path, themeColor: TOOL_CONFIG.COMPATIBILITY.themeColor, icon: <TOOL_CONFIG.COMPATIBILITY.Icon className="w-4 h-4" /> },
  { label: TOOL_CONFIG.MY_CHECK.label,      path: TOOL_CONFIG.MY_CHECK.path,      themeColor: TOOL_CONFIG.MY_CHECK.themeColor,      icon: <TOOL_CONFIG.MY_CHECK.Icon className="w-4 h-4" /> },
  { label: TOOL_CONFIG.MY_BUILDER.label,    path: TOOL_CONFIG.MY_BUILDER.path,    themeColor: TOOL_CONFIG.MY_BUILDER.themeColor,    icon: <TOOL_CONFIG.MY_BUILDER.Icon className="w-4 h-4" /> },
  // Browse components
  ...COMPONENT_ORDER.map(type => {
    const config = UNIFIED_COMPONENT_CONFIG[type]
    return {
      label: config.plural,
      path: config.path,
      themeColor: config.themeColor,
      icon: componentIconMap[config.icon] ?? null,
    }
  }),
]

// Mobile icon background colors per theme
const MOBILE_ICON_BG: Record<ThemeColor, string> = {
  blue:   'bg-blue-100 text-blue-600',
  purple: 'bg-purple-100 text-purple-600',
  green:  'bg-green-100 text-green-600',
  red:    'bg-red-100 text-red-600',
  gray:   'bg-gray-100 text-gray-600',
  orange: 'bg-orange-100 text-orange-600',
  yellow: 'bg-yellow-100 text-yellow-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  teal:   'bg-teal-100 text-teal-600',
  violet: 'bg-violet-100 text-violet-600',
  emerald:'bg-emerald-100 text-emerald-600',
}

const TOOL_PATHS = new Set(['/compatibility-checker', '/my-check', '/my-builder'])

// Static Tailwind class maps (JIT won't work with dynamic template strings)
const ACTIVE_BG: Record<ThemeColor, string> = {
  blue:   'bg-blue-100 text-blue-700',
  purple: 'bg-purple-100 text-purple-700',
  green:  'bg-green-100 text-green-700',
  red:    'bg-red-100 text-red-700',
  gray:   'bg-gray-200 text-gray-800',
  orange: 'bg-orange-100 text-orange-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  teal:   'bg-teal-100 text-teal-700',
  violet: 'bg-violet-100 text-violet-700',
  emerald:'bg-emerald-100 text-emerald-700',
}


export const GlobalNav: React.FC = () => {
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false)
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const mobileUserMenuRef = useRef<HTMLDivElement>(null)
  const { isVisible, isScrolledPast } = useScrollDirection()
  const { status, user, loginWithGoogle, logout } = useAuth()

  const shouldHide = !isVisible && !mobileOpen
  const needsTransform = isScrolledPast || shouldHide

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  // Close user menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
      if (mobileUserMenuRef.current && !mobileUserMenuRef.current.contains(e.target as Node)) {
        setMobileUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOutClick = () => {
    setUserMenuOpen(false)
    setMobileUserMenuOpen(false)
    setMobileOpen(false)
    setSignOutConfirmOpen(true)
  }

  const handleSignOutConfirm = async () => {
    setSignOutConfirmOpen(false)
    await logout()
  }

  // Mobile auth icon — shown to the left of the hamburger button
  const MobileAuthButton: React.FC = () => {
    if (status === 'loading') return null

    if (status === 'unauthenticated') {
      return (
        <button
          onClick={() => loginWithGoogle(location.pathname)}
          className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label="Sign in"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
        </button>
      )
    }

    return (
      <div ref={mobileUserMenuRef} className="relative">
        <button
          onClick={() => setMobileUserMenuOpen(v => !v)}
          className="p-1 rounded-full hover:ring-2 hover:ring-gray-200 transition-all"
          aria-label="User menu"
        >
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center">
              <span className="text-white text-xs font-bold">
                {user?.displayName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </button>

        {mobileUserMenuOpen && (
          <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-50">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.displayName}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
            <Link
              to="/settings"
              onClick={() => setMobileUserMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </Link>
            <button
              onClick={() => { handleSignOutClick() }}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    )
  }

  const UserWidget: React.FC<{ mobile?: boolean }> = ({ mobile }) => {
    if (status === 'loading') return null

    if (status === 'unauthenticated') {
      return (
        <button
          onClick={() => loginWithGoogle(location.pathname)}
          className={`${mobile ? 'w-full text-left px-4 py-3 text-sm border-l-4 border-transparent' : 'px-3 py-1.5 text-sm rounded-md'} text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors`}
        >
          Sign in
        </button>
      )
    }

    if (mobile) {
      return (
        <div className="border-t border-gray-200 mt-1 pt-1">
          <div className="px-4 py-2 text-xs text-gray-500 truncate">{user?.email}</div>
          <Link
            to="/settings"
            onClick={() => setMobileOpen(false)}
            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 border-l-4 border-transparent"
          >
            Settings
          </Link>
          <button
            onClick={() => { handleSignOutClick() }}
            className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 border-l-4 border-transparent"
          >
            Sign out
          </button>
        </div>
      )
    }

    return (
      <div ref={userMenuRef} className="relative ml-2 shrink-0">
        <button
          onClick={() => setUserMenuOpen(v => !v)}
          className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors"
          aria-label="User menu"
        >
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="w-6 h-6 rounded-full" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
              <span className="text-white text-xs font-bold">
                {user?.displayName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <span className="text-sm text-gray-700 max-w-[120px] truncate hidden xl:block">
            {user?.displayName}
          </span>
        </button>

        {userMenuOpen && (
          <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-50">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.displayName}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
            <Link
              to="/settings"
              onClick={() => setUserMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </Link>
            <button
              onClick={() => { handleSignOutClick() }}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
    <nav
      className={`bg-white border-b border-gray-200 sticky top-0 z-50 ${
        needsTransform
          ? `transition-transform duration-300 ease-in-out ${shouldHide ? '-translate-y-full' : 'translate-y-0'}`
          : ''
      }`}
    >
      {/* Desktop navigation */}
      <div className="hidden lg:flex items-center px-4 h-12">
        <Link
          to="/"
          className="flex items-center mr-4 shrink-0"
        >
          <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <span className="ml-2 font-bold text-gray-900 text-sm whitespace-nowrap">
            Check My Rig
          </span>
        </Link>

        <div className="flex items-center overflow-x-auto flex-1 min-w-0">
          {/* Tools */}
          <div className="flex items-center space-x-0.5 shrink-0">
            {NAV_ITEMS.filter(i => TOOL_PATHS.has(i.path)).map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-2.5 py-1.5 text-sm rounded-md whitespace-nowrap transition-colors ${
                  isActive(item.path)
                    ? `${ACTIVE_BG[item.themeColor]} font-medium`
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
          {/* Divider */}
          <div className="mx-2 h-4 w-px bg-gray-200 shrink-0" />
          {/* Browse */}
          <div className="flex items-center space-x-0.5 overflow-x-auto">
            {NAV_ITEMS.filter(i => !TOOL_PATHS.has(i.path)).map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-2.5 py-1.5 text-sm rounded-md whitespace-nowrap transition-colors ${
                  isActive(item.path)
                    ? `${ACTIVE_BG[item.themeColor]} font-medium`
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        <UserWidget />
      </div>

      {/* Mobile navigation */}
      <div className="lg:hidden flex items-center justify-between px-4 h-12">
        <Link to="/" className="flex items-center" onClick={() => setMobileOpen(false)}>
          <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <span className="ml-2 font-bold text-gray-900 text-sm">Check My Rig</span>
        </Link>
        <div className="flex items-center gap-1">
          <MobileAuthButton />
          <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          aria-label="Toggle navigation menu"
        >
          {mobileOpen ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
        </div>
      </div>

      {/* Mobile dropdown — 2-column icon grid */}
      <div
        className={`lg:hidden border-gray-200 bg-white overflow-hidden transition-[max-height] duration-300 ease-in-out ${
          mobileOpen ? 'max-h-[80vh] border-t overflow-y-auto' : 'max-h-0'
        }`}
      >
        <div className="px-4 py-3 space-y-4">

            {/* Tools section */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Tools</p>
              <div className="grid grid-cols-2 gap-2">
                {NAV_ITEMS.filter(i => TOOL_PATHS.has(i.path)).map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-colors ${
                      isActive(item.path)
                        ? `${ACTIVE_BG[item.themeColor]} border-transparent font-medium`
                        : 'bg-gray-50 border-gray-100 hover:bg-gray-100 border-transparent'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${MOBILE_ICON_BG[item.themeColor]}`}>
                      {item.icon}
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-tight">{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Browse section */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Browse</p>
              <div className="grid grid-cols-2 gap-2">
                {NAV_ITEMS.filter(i => !TOOL_PATHS.has(i.path)).map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-colors ${
                      isActive(item.path)
                        ? `${ACTIVE_BG[item.themeColor]} border-transparent font-medium`
                        : 'bg-gray-50 border-gray-100 hover:bg-gray-100 border-transparent'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${MOBILE_ICON_BG[item.themeColor]}`}>
                      {item.icon}
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-tight">{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>

          </div>
          <UserWidget mobile />
      </div>
    </nav>

    {/* Mobile menu backdrop */}
    {mobileOpen && (
      <div
        className="lg:hidden fixed inset-0 z-40 bg-black/30"
        onClick={() => setMobileOpen(false)}
      />
    )}

    {/* Sign-out confirmation modal */}
    {signOutConfirmOpen && (
      <div className="fixed inset-0 z-[200] flex items-center justify-center">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40"
          onClick={() => setSignOutConfirmOpen(false)}
        />
        {/* Dialog */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-2">Sign out</h2>
          <p className="text-sm text-gray-600 mb-6">Are you sure you want to sign out?</p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setSignOutConfirmOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => { void handleSignOutConfirm() }}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}
